import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem, Typography,
  Table, TableHead, TableRow, TableCell, TableBody, Alert, IconButton, Chip, Box,
} from '@mui/material';
import { Close, PersonAdd } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { teamUsersApi, type PortalResource } from '../api/teamUsers.api';
import { extractApiError } from '../api/error';
import { useVertical } from '../hooks/useVertical';

interface Props {
  open: boolean;
  onClose: () => void;
  resource: PortalResource; // 'dealers' | 'clients'
  entityId: number | null;
  entityName?: string;
}

/**
 * Admin panel to view + add the login users mapped to one dealer/client. Every user added
 * here shares that entity's role and data scope. New logins get the default password Gig4ce@123.
 */
export function PortalUsersDialog({ open, onClose, resource, entityId, entityName }: Props) {
  const qc = useQueryClient();
  const { activeVertical, verticals } = useVertical();
  // The Ops/Finance approver is an Asset Management payment-workflow concept — show it ONLY in AM
  // context (active vertical = AM, or an AM-only user). It must never appear for PDI/general client
  // management, keeping each vertical's logic isolated.
  const isAmContext = activeVertical?.code === 'asset_management' || (verticals.length === 1 && verticals[0]?.code === 'asset_management');
  const showApprover = resource === 'clients' && isAmContext;
  const label = resource === 'dealers' ? 'dealer' : 'client';
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [approverType, setApproverType] = useState<'' | 'ops' | 'finance'>('');
  const [created, setCreated] = useState<{ email: string; password: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: users, isLoading } = useQuery({
    queryKey: ['portal-users', resource, entityId],
    queryFn: () => teamUsersApi.list(resource, entityId as number),
    enabled: open && entityId != null,
  });

  const addMut = useMutation({
    mutationFn: () => teamUsersApi.add(resource, entityId as number, { email: email.trim(), fullName: fullName.trim() || undefined, approverType: showApprover && approverType ? approverType : undefined }),
    onSuccess: (res) => {
      setCreated({ email: res.email, password: res.temporaryPassword });
      setEmail(''); setFullName(''); setApproverType(''); setError(null);
      qc.invalidateQueries({ queryKey: ['portal-users', resource, entityId] });
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const close = () => { setCreated(null); setError(null); setEmail(''); setFullName(''); onClose(); };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>
        Users — {entityName ?? label}
        <IconButton onClick={close} sx={{ position: 'absolute', right: 8, top: 8 }}><Close /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info">
            Add team members who can log in for this {label}. They sign in at the normal login page and
            see the same data (same access). New logins get the default password <b>Gig4ce@123</b>.
          </Alert>

          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Table size="small">
              <TableHead><TableRow><TableCell>Email</TableCell><TableCell>Name</TableCell>{showApprover && <TableCell>Approver</TableCell>}<TableCell>Status</TableCell></TableRow></TableHead>
              <TableBody>
                {isLoading && <TableRow><TableCell colSpan={4}>Loading…</TableCell></TableRow>}
                {!isLoading && (users?.length ?? 0) === 0 && <TableRow><TableCell colSpan={4}>No login users yet.</TableCell></TableRow>}
                {users?.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>{u.fullName}</TableCell>
                    {showApprover && <TableCell>{u.approverType ? <Chip size="small" variant="outlined" label={u.approverType === 'finance' ? 'Finance' : 'Ops'} /> : '—'}</TableCell>}
                    <TableCell><Chip size="small" label={u.status} color={u.status === 'active' ? 'success' : 'default'} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {created && (
            <Alert severity="success" onClose={() => setCreated(null)}>
              Login created — <b>{created.email}</b> / password <b>{created.password}</b>. Share it securely; the user changes it on first sign-in.
            </Alert>
          )}
          {error && <Alert severity="error">{error}</Alert>}

          <Typography variant="subtitle2">Add a user</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField size="small" label="Email" value={email} onChange={(e) => setEmail(e.target.value)} sx={{ flex: 1.5 }} />
            <TextField size="small" label="Name (optional)" value={fullName} onChange={(e) => setFullName(e.target.value)} sx={{ flex: 1 }} />
            {showApprover && (
              <TextField select size="small" label="Approver" value={approverType} onChange={(e) => setApproverType(e.target.value as '' | 'ops' | 'finance')} sx={{ minWidth: 120 }}>
                <MenuItem value="">—</MenuItem>
                <MenuItem value="ops">Ops</MenuItem>
                <MenuItem value="finance">Finance</MenuItem>
              </TextField>
            )}
            <Button variant="contained" startIcon={<PersonAdd />} disabled={!email.trim() || addMut.isPending} onClick={() => addMut.mutate()}>Add</Button>
          </Stack>
          {showApprover && <Typography variant="caption" color="text.secondary">Payment approvals: an <b>Ops</b> user approves first, then a <b>Finance</b> user.</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Done</Button>
      </DialogActions>
    </Dialog>
  );
}
